// src/redux/services/store/storeProductsApi.ts
import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import { baseApi } from "../baseApi";

export type ProductType = "simple" | "variable";

/** ---------- LIST (cards) ---------- */
export type StoreProductImageOut = {
  cdn_url?: string | null;
  alt_text?: string | null;
};

export type StoreProductCardColorPreviewOut = {
  value_id?: number | null;
  value_code?: string | null;
  label?: string | null;
  image?: StoreProductImageOut;
};

export type StoreProductCardOut = {
  id: number;
  name: string;
  slug: string;

  product_type: ProductType;
  is_featured?: boolean;
  is_customizable?: boolean;
  card_variant_id?: number | null;
  can_direct_add_to_cart?: boolean;

  image?: StoreProductImageOut;
  color_variant_images?: StoreProductCardColorPreviewOut[];

  price_from?: string;
  compare_at_from?: string | null;
  currency_code?: string;

  in_stock?: boolean;
};

export type StoreBrandFilterOptionOut = {
  id: number;
  label: string;
  slug?: string | null;
  count: number;
};

export type StoreAttributeFilterValueOut = {
  id: number;
  label: string;
  value_code?: string | null;
  count: number;
  hex_color?: string | null;
  image_url?: string | null;
  swatch_image_url?: string | null;
};

export type StoreAttributeFilterGroupOut = {
  attribute_id: number;
  code: string;
  label: string;
  display_layout?: string;
  values: StoreAttributeFilterValueOut[];
};

export type StorePriceFilterOut = {
  min: string | number;
  max: string | number;
};

export type StoreStockFilterOut = {
  in_stock: number;
  out_of_stock: number;
};

export type StoreDiscountFilterOptionOut = {
  value: number;
  label: string;
  count: number;
};

export type StoreProductAvailableFiltersOut = {
  brands: StoreBrandFilterOptionOut[];
  attributes: StoreAttributeFilterGroupOut[];
  price?: StorePriceFilterOut | null;
  stock?: StoreStockFilterOut | null;
  discount_ranges: StoreDiscountFilterOptionOut[];
};

export type StoreProductListResponse = {
  items: StoreProductCardOut[];
  total: number;
  available_filters?: StoreProductAvailableFiltersOut;
};

export type StoreProductSort = "newest" | "price_asc" | "price_desc" | "popular";

export type StoreListProductsArgs = {
  skip?: number;
  limit?: number;

  q?: string | null;

  category_id?: number | null;
  category_slug?: string | null;

  brand_id?: number | null;
  is_featured?: boolean | null;
  price_min?: number | null;
  price_max?: number | null;
  discount_min?: number | null;
  attribute_value_ids?: number[] | null;

  sort?: StoreProductSort | string | null;
  include_total?: boolean | null;
  include_filters?: boolean | null;
};

/** ---------- DETAIL (PDP) ---------- */
export type StoreProductDetailImage = {
  cdn_url: string;
  alt_text?: string | null;
  sort_order?: number;
  is_primary?: boolean;
};

export type StoreProductDetailVariant = {
  id: number;
  sku: string;
  attributes_json?: Record<string, any> | null;

  price: string;
  compare_at_price?: string | null;
  currency_code: string;

  is_active: boolean;
  images: StoreProductDetailImage[];
};

export type StoreAllowedColor = {
  id: number;
  name: string;
  slug: string;
  hex?: string | null;
  sort_order?: number;
};

export type StoreProductDetail = {
  id: number;
  name: string;
  slug: string;

  category_ids: number[];

  product_type: ProductType;
  status: string;
  product_kind: string;

  short_description?: string | null;
  description?: string | null;
  content?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string | null;

  is_customizable?: boolean;
  allowed_colors?: StoreAllowedColor[];

  variants: StoreProductDetailVariant[];
  images?: StoreProductDetailImage[];
};

function cleanParams<T extends Record<string, any>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    (out as any)[k] = v;
  }
  return out;
}

type Builder = EndpointBuilder<any, any, any>;

const emptyAvailableFilters = (): StoreProductAvailableFiltersOut => ({
  brands: [],
  attributes: [],
  price: null,
  stock: { in_stock: 0, out_of_stock: 0 },
  discount_ranges: [],
});

export const storeProductsApi = baseApi.injectEndpoints({
  endpoints: (builder: Builder) => ({
    listStoreProducts: builder.query<StoreProductListResponse, StoreListProductsArgs>({
      query: ({
        skip = 0,
        limit = 24,
        q = null,
        category_id = null,
        category_slug = null,
        brand_id = null,
        is_featured = null,
        price_min = null,
        price_max = null,
        discount_min = null,
        attribute_value_ids = null,
        sort = null,
        include_total = null,
        include_filters = null,
      }) => ({
        url: `/store/products`,
        method: "GET",
        params: cleanParams({
          skip,
          limit,
          q,
          category_id,
          category_slug,
          brand_id,
          is_featured,
          price_min,
          price_max,
          discount_min,
          attribute_value_ids,
          sort,
          include_total,
          include_filters,
        }),
      }),

      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { skip, limit, ...rest } = (queryArgs || {}) as any;
        return `${endpointName}:${JSON.stringify(rest)}`;
      },

      merge: (currentCache, newData, { arg }) => {
        const incoming = newData?.items ?? [];

        if (!currentCache.items) currentCache.items = [];
        if (!currentCache.available_filters) {
          currentCache.available_filters = emptyAvailableFilters();
        }

        if ((arg?.skip ?? 0) === 0) {
          currentCache.items = incoming;
          currentCache.total = newData.total;
          currentCache.available_filters =
            newData.available_filters ?? emptyAvailableFilters();
          return;
        }

        const seen = new Set(currentCache.items.map((x) => x.id));
        for (const it of incoming) {
          if (!seen.has(it.id)) currentCache.items.push(it);
        }

        currentCache.total = newData.total;

        if (newData.available_filters) {
          currentCache.available_filters = newData.available_filters;
        }
      },

      forceRefetch: ({ currentArg, previousArg }) => {
        const a = { ...(currentArg || {}) } as any;
        const b = { ...(previousArg || {}) } as any;
        delete a.skip;
        delete a.limit;
        delete b.skip;
        delete b.limit;
        return JSON.stringify(a) !== JSON.stringify(b);
      },

      transformResponse: (response: StoreProductListResponse): StoreProductListResponse => ({
        ...response,
        items: response?.items ?? [],
        total: response?.total ?? 0,
        available_filters: response?.available_filters ?? emptyAvailableFilters(),
      }),

      providesTags: () => [{ type: "StoreProductList", id: "LIST" }],
    }),

    listStoreProductsPage: builder.query<StoreProductListResponse, StoreListProductsArgs>({
      query: ({
        skip = 0,
        limit = 24,
        q = null,
        category_id = null,
        category_slug = null,
        brand_id = null,
        is_featured = null,
        price_min = null,
        price_max = null,
        discount_min = null,
        attribute_value_ids = null,
        sort = null,
        include_total = null,
        include_filters = null,
      }) => ({
        url: `/store/products`,
        method: "GET",
        params: cleanParams({
          skip,
          limit,
          q,
          category_id,
          category_slug,
          brand_id,
          is_featured,
          price_min,
          price_max,
          discount_min,
          attribute_value_ids,
          sort,
          include_total,
          include_filters,
        }),
      }),
      transformResponse: (response: StoreProductListResponse): StoreProductListResponse => ({
        ...response,
        items: response?.items ?? [],
        total: response?.total ?? 0,
        available_filters: response?.available_filters ?? emptyAvailableFilters(),
      }),
      providesTags: () => [{ type: "StoreProductList", id: "PAGE" }],
    }),

    getStoreProductBySlug: builder.query<StoreProductDetail, { slug: string }>({
      query: ({ slug }: { slug: string }) => ({
        url: `/store/products/${encodeURIComponent(slug)}`,
        method: "GET",
      }),
      providesTags: (_result: StoreProductDetail | undefined, _err: unknown, arg: { slug: string }) => [
        { type: "StoreProduct", id: arg.slug },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListStoreProductsQuery,
  useListStoreProductsPageQuery,
  useGetStoreProductBySlugQuery,
} = storeProductsApi;
